import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, Euro, Download, TrendingUp, LayoutDashboard, Settings, LogOut, CalendarDays, ChevronRight, User, Calendar, Copy, RefreshCcw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import Logo from '../components/Logo';

const AdminRevenue = () => {
  const navigate = useNavigate();
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConciergeDetails, setSelectedConciergeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('completed'); // 'all', 'completed', 'refunded'

  // Générer les 12 derniers mois
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return months;
  };

  useEffect(() => {
    fetchRevenue();
  }, [selectedMonth]);

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      const params = selectedMonth !== 'all' ? `?month=${selectedMonth}` : '';
      const response = await api.get(`/admin/revenue-by-concierge${params}`);
      setRevenueData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des revenus');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('IBAN copié !');
  };

  const exportToCSV = () => {
    if (!revenueData || !revenueData.concierges.length) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    const monthLabel = selectedMonth === 'all' ? 'tous' : selectedMonth;
    const headers = ['Concierge', 'Email', 'Entreprise', 'IBAN', 'Montant Dû (€)', 'Revenue App (€)', 'Transactions'];
    const rows = revenueData.concierges.map(c => [
      c.concierge_nom,
      c.concierge_email,
      c.concierge_entreprise,
      c.concierge_iban,
      c.total_du,
      c.total_app_gagne,
      c.nb_transactions
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `revenus-concierges-${monthLabel}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Export CSV téléchargé !');
  };

  const openConciergeDetails = async (conciergeId) => {
    setLoadingDetails(true);
    setDetailDialogOpen(true);
    try {
      const monthParam = selectedMonth !== 'all' ? `month=${selectedMonth}` : '';
      const statusParam = `status_filter=${statusFilter}`;
      const params = [monthParam, statusParam].filter(Boolean).join('&');
      const response = await api.get(`/admin/revenue/concierge/${conciergeId}?${params}`);
      setSelectedConciergeDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading && !revenueData) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <Logo variant="full" size="h-12" />
        </div>
        <nav className="space-y-2">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <LayoutDashboard className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Dashboard
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/options')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Settings className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Options Globales
          </Button>
          <Button variant="ghost" className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100">
            <Euro className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Paiements Concierges
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/concierges')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Users className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Liste Concierges
          </Button>
        </nav>
        
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={() => { localStorage.clear(); navigate('/auth'); }}
            className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Déconnexion
          </Button>
        </div>
      </aside>

      <div className="ml-64">
        <header className="bg-white border-b border-slate-100 px-8 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Paiements Concierges
            </h1>
            <p className="text-base text-slate-600 mt-1">
              {selectedMonth === 'all' ? 'Tous les mois' : `Mois de ${getMonthOptions().find(m => m.value === selectedMonth)?.label || selectedMonth}`}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-slate-400" />
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px]" data-testid="month-selector">
                  <SelectValue placeholder="Sélectionner un mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {getMonthOptions().map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="status-filter">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Payées</SelectItem>
                  <SelectItem value="refunded">Remboursées</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={exportToCSV}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Exporter CSV
            </Button>
          </div>
        </header>

        <main className="p-8 space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Total à payer aux concierges</p>
                  <p className="text-3xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {(revenueData?.total_a_payer_concierges || 0).toFixed(2)} €
                  </p>
                </div>
                <Euro className="w-10 h-10 text-rose-600" strokeWidth={1.5} />
              </div>
            </Card>

            <Card className="bg-rose-50 border border-rose-200 shadow-sm rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-rose-600 mb-1">Vos revenus (app)</p>
                  <p className="text-3xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {(revenueData?.total_revenue_app || 0).toFixed(2)} €
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-rose-600" strokeWidth={1.5} />
              </div>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Concierges actifs</p>
                  <p className="text-3xl font-bold text-slate-700 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {revenueData?.nb_concierges_actifs || 0}
                  </p>
                </div>
                <Users className="w-10 h-10 text-slate-400" strokeWidth={1.5} />
              </div>
            </Card>
          </div>

          {/* Concierges List */}
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-6">
            <h2 className="text-xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Détail par Concierge
              {selectedMonth !== 'all' && (
                <span className="ml-2 text-sm font-normal text-rose-600 bg-rose-50 px-2 py-1 rounded">
                  {getMonthOptions().find(m => m.value === selectedMonth)?.label}
                </span>
              )}
            </h2>

            {loading ? (
              <div className="text-center py-12">
                <div className="text-slate-500">Chargement...</div>
              </div>
            ) : !revenueData?.concierges || revenueData.concierges.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">
                  {selectedMonth === 'all' 
                    ? 'Aucun concierge avec des revenus pour le moment'
                    : 'Aucune transaction pour ce mois'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Concierge</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">Entreprise</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-700">IBAN</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">Payées</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">Remb.</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">Montant Dû</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-700">Revenue App</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-700"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueData.concierges.map((concierge, index) => (
                      <tr 
                        key={concierge.concierge_id} 
                        onClick={() => openConciergeDetails(concierge.concierge_id)}
                        className={`border-b border-slate-100 hover:bg-rose-50 transition-colors cursor-pointer ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{concierge.concierge_nom}</p>
                            <p className="text-sm text-slate-500">{concierge.concierge_email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-600">{concierge.concierge_entreprise}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                              {concierge.concierge_iban}
                            </code>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyToClipboard(concierge.concierge_iban); }}
                              className="p-1 hover:bg-rose-100 rounded transition-colors"
                              title="Copier l'IBAN"
                            >
                              <Copy className="w-3 h-3 text-slate-500" />
                            </button>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right text-slate-700">{concierge.nb_transactions}</td>
                        <td className="py-4 px-4 text-right">
                          {concierge.nb_refunded > 0 ? (
                            <span className="text-amber-600 font-medium">{concierge.nb_refunded}</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-bold text-rose-600 tabular-nums">
                            {(concierge.total_du || 0).toFixed(2)} €
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-medium text-rose-400 tabular-nums">
                            {(concierge.total_app_gagne || 0).toFixed(2)} €
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-slate-300 bg-rose-50">
                    <tr>
                      <td colSpan="4" className="py-4 px-4 text-right font-bold text-slate-900">
                        TOTAUX
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-rose-600 tabular-nums text-lg">
                        {(revenueData.total_a_payer_concierges || 0).toFixed(2)} €
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-rose-400 tabular-nums text-lg">
                        {(revenueData.total_revenue_app || 0).toFixed(2)} €
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>

          {/* Instructions */}
          <Card className="bg-rose-50 border border-rose-200 rounded-xl p-6">
            <h3 className="text-lg font-medium text-rose-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Comment payer les concierges
            </h3>
            <ul className="space-y-2 text-sm text-rose-800">
              <li>1. Sélectionnez le mois concerné avec le sélecteur ci-dessus</li>
              <li>2. Cliquez sur un concierge pour voir le détail de ses ventes</li>
              <li>3. Exportez le CSV avec le bouton "Exporter CSV"</li>
              <li>4. Effectuez les virements bancaires vers les IBANs indiqués</li>
            </ul>
          </Card>
        </main>
      </div>

      {/* Dialog détails des ventes par concierge */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[750px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Détails - {selectedConciergeDetails?.concierge_nom || 'Concierge'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-8 text-center text-slate-500">Chargement...</div>
          ) : selectedConciergeDetails ? (
            <div className="space-y-4">
              {/* Info concierge */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedConciergeDetails.concierge_email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Entreprise</p>
                    <p className="font-medium">{selectedConciergeDetails.concierge_entreprise}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">IBAN</p>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">{selectedConciergeDetails.concierge_iban}</code>
                  </div>
                </div>
              </div>
              
              {/* Résumé */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-rose-50 rounded-lg text-center">
                  <p className="text-xs text-rose-600 mb-1">À payer (net)</p>
                  <p className="text-xl font-bold text-rose-600">{(selectedConciergeDetails.total_concierge || 0).toFixed(2)} €</p>
                </div>
                <div className="p-3 bg-rose-100 rounded-lg text-center">
                  <p className="text-xs text-rose-500 mb-1">Vos revenus (net)</p>
                  <p className="text-xl font-bold text-rose-500">{(selectedConciergeDetails.total_app || 0).toFixed(2)} €</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-600 mb-1">Transactions</p>
                  <p className="text-xl font-bold text-slate-700">{selectedConciergeDetails.total_transactions}</p>
                </div>
              </div>
              
              {/* Info remboursements si présents */}
              {selectedConciergeDetails.refund_count > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800 text-sm mb-2">
                    <RefreshCcw className="w-4 h-4" />
                    <span className="font-medium">{selectedConciergeDetails.refund_count} transaction(s) remboursée(s)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-amber-600">Montant remboursé</p>
                      <p className="font-semibold text-amber-800">{(selectedConciergeDetails.total_refunded || 0).toFixed(2)} €</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Part concierge</p>
                      <p className="font-semibold text-amber-800">{(selectedConciergeDetails.total_refunded_concierge || 0).toFixed(2)} €</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Part Plus</p>
                      <p className="font-semibold text-amber-800">{(selectedConciergeDetails.total_refunded_app || 0).toFixed(2)} €</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">Ces montants ne sont pas inclus dans le total à payer.</p>
                </div>
              )}
              
              {/* Liste des ventes */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Détail des transactions</h4>
                {selectedConciergeDetails.sales.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucune transaction pour cette période</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {selectedConciergeDetails.sales.map((sale) => (
                      <div 
                        key={sale.id} 
                        className={`p-3 border rounded-lg ${
                          sale.status === 'refunded' 
                            ? 'border-amber-200 bg-amber-50' 
                            : 'border-slate-100 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{sale.option_name}</span>
                              {sale.status === 'refunded' && (
                                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <RefreshCcw className="w-3 h-3" />
                                  Remboursé
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{sale.property_name}</p>
                          </div>
                          <div className="text-right">
                            {sale.status === 'refunded' ? (
                              <>
                                <span className="font-bold text-amber-600 line-through">{(sale.montant_concierge || 0).toFixed(2)} €</span>
                                <p className="text-xs text-amber-500">Non comptabilisé</p>
                              </>
                            ) : (
                              <>
                                <span className="font-bold text-rose-600">{(sale.montant_concierge || 0).toFixed(2)} €</span>
                                <p className="text-xs text-rose-400">App: {(sale.montant_app || 0).toFixed(2)} €</p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {sale.voyageur_nom}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {sale.date_debut ? new Date(sale.date_debut).toLocaleDateString('fr-FR') : '-'}
                          </span>
                          <span className="text-slate-400">
                            Prix payé: {(sale.prix_paye || 0).toFixed(2)} €
                          </span>
                          {sale.status === 'refunded' && sale.refunded_at && (
                            <span className="text-amber-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Remboursé le {new Date(sale.refunded_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRevenue;
